import {
  Text,
  useToast,
  Divider,
  Flex,
  Box,
  Button,
  useDisclosure,
  InputGroup,
  Input,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { Date, Util } from '@finley-technologies/finley-types';
import { commonFormulas } from '@finley-technologies/formula-engine';

import { DeleteConfirmationModal } from '../../../delete/DeleteArrayPropertyButton';
import { LenderPostgresQueryDateView } from './LenderPostgresQueryComponents';
import { QueryInputTypeBadge } from './QueryInputTypeBadge';
import { CalculationTerminologiesPopover } from '~/core/routes/calculation-terminologies/CalculationTerminologiesPopover';
import { colors } from '~/packages/common-frontend/colors';
import {
  CopyIconButton,
  DeleteIconButton,
  EditIconButton,
} from '~/packages/common-frontend/components/IconButton';
import { Select } from '~/packages/common-frontend/components/Select';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { CreditAgreement } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { PreviewPostgresQueryButton } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantPostgresQuery/PreviewPostgresQueryButton';
import { LenderReportingPeriod } from '~/packages/creditAgreements/ui/ReportingPeriod';
import { WipConfig } from '~/packages/creditAgreements/ui/types';
import { AddQueryInputModal } from '~/packages/settings/ui/AddQueryInputModal';

type OrgProvidedInputSelectFieldProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  orgProvidedInputQuery: CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery;
};
const OrgProvidedInputKeysSelectField = ({
  wipConfig,
  fullPropertyKey,
  onSave,
  isSaveLoading,
  orgProvidedInputQuery,
}: OrgProvidedInputSelectFieldProps) => {
  const toast = useToast();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const initialInputKey = orgProvidedInputQuery.parameterConfig.key.key;
  const [selectedInputKey, setSelectedInputKey] = useState<string | undefined>(
    initialInputKey,
  );

  const selectOptions:
    | {
        value: string;
        label: string;
      }[]
    | null = useMemo(() => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return null;
    }

    return wipConfig.orgProvidedInputKeys.map((inputKey) => ({
      value: inputKey,
      label: inputKey,
    }));
  }, [wipConfig, fullPropertyKey]);

  return (
    <Box
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {isEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
        <>
          {selectOptions && !!selectOptions.length && (
            <Flex alignItems="center">
              <Select
                value={selectedInputKey}
                onChange={(val) => {
                  setSelectedInputKey(val);
                }}
                options={selectOptions}
                width="xl"
              />
              <Button
                color={colors.aurora[400]}
                border="2px"
                borderRadius="8px"
                borderColor={colors.aurora[400]}
                backgroundColor="white"
                onClick={() => {
                  setSelectedInputKey(initialInputKey);
                  onEditClose();
                }}
                ml="32px"
                mr="24px"
              >
                Cancel
              </Button>
              <Button
                color="white"
                backgroundColor={colors.aurora[400]}
                isLoading={isSaveLoading}
                onClick={async () => {
                  try {
                    if (selectedInputKey) {
                      const updatedQuery = _.cloneDeep(orgProvidedInputQuery);
                      updatedQuery.parameterConfig.key.key = selectedInputKey;

                      await onSave(updatedQuery);
                    }
                  } catch (err) {
                    toast({
                      ...errorToastConfig,
                      id: 'saveError',
                      title: 'Error saving formula. Please try again.',
                    });
                  } finally {
                    onEditClose();
                  }
                }}
              >
                Save
              </Button>
            </Flex>
          )}
        </>
      ) : (
        <Flex width="100%" alignItems="center" justifyContent="space-between">
          <Text>{initialInputKey}</Text>
          <EditIconButton label="Edit" ariaLabel="edit" onClick={onEditOpen} />
        </Flex>
      )}
    </Box>
  );
};

type OrgProvidedInputReportingDateFormulaFieldProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  orgProvidedInputQuery: CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery;
  triggerDate?: Util.FullDateStr;
  evaluatedDate?: Util.FullDateStr;
};
const OrgProvidedInputReportingDateFormulaField = ({
  wipConfig,
  fullPropertyKey,
  onSave,
  isSaveLoading,
  orgProvidedInputQuery,
  triggerDate,
  evaluatedDate,
}: OrgProvidedInputReportingDateFormulaFieldProps) => {
  const toast = useToast();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const initialFieldValue =
    orgProvidedInputQuery.parameterConfig.reportingDateFormula.formula;

  const [fieldValue, setFieldValue] = useState<string>(initialFieldValue);

  return (
    <Box
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {isEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
        <>
          <InputGroup mb="24px">
            <Input
              p="24px"
              type="text"
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
            />
          </InputGroup>
          <Flex width="100%" alignItems="center" justifyContent="flex-end">
            <Button
              color={colors.aurora[400]}
              border="2px"
              borderRadius="8px"
              borderColor={colors.aurora[400]}
              backgroundColor="white"
              onClick={() => {
                setFieldValue(initialFieldValue);
                onEditClose();
              }}
              mr="24px"
            >
              Cancel
            </Button>
            <Button
              color="white"
              backgroundColor={colors.aurora[400]}
              isLoading={isSaveLoading}
              onClick={async () => {
                try {
                  const updatedQuery = _.cloneDeep(orgProvidedInputQuery);
                  updatedQuery.parameterConfig.reportingDateFormula.formula = fieldValue;

                  await onSave(updatedQuery);
                } catch (err) {
                  toast({
                    ...errorToastConfig,
                    id: 'saveError',
                    title: 'Error saving formula. Please try again.',
                  });
                } finally {
                  onEditClose();
                }
              }}
            >
              Save
            </Button>
          </Flex>
        </>
      ) : (
        <LenderPostgresQueryDateView
          initialFieldValue={initialFieldValue}
          onEditOpen={onEditOpen}
          evaluatedDate={evaluatedDate}
          triggerDate={triggerDate}
          dateType="reportingDate"
        />
      )}
    </Box>
  );
};

type OrgProvidedInputReportingPeriodFieldProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  orgProvidedInputQuery: CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery;
};
const OrgProvidedInputReportingPeriodField = ({
  wipConfig,
  fullPropertyKey,
  onSave,
  isSaveLoading,
  orgProvidedInputQuery,
}: OrgProvidedInputReportingPeriodFieldProps) => {
  const toast = useToast();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const initialReportingPeriod =
    orgProvidedInputQuery.parameterConfig.reportingPeriod.reportingPeriod;

  const [
    reportingPeriod,
    setReportingPeriod,
  ] = useState<Date.Reporting.ReportingPeriod>(initialReportingPeriod);

  return (
    <Box
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {isEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
        <>
          <Flex>
            <LenderReportingPeriod
              reportingPeriod={reportingPeriod}
              setReportingPeriod={setReportingPeriod}
            />
            <Button
              color={colors.aurora[400]}
              border="2px"
              borderRadius="8px"
              borderColor={colors.aurora[400]}
              backgroundColor="white"
              onClick={() => {
                setReportingPeriod(initialReportingPeriod);
                onEditClose();
              }}
              ml="32px"
              mr="24px"
            >
              Cancel
            </Button>
            <Button
              color="white"
              backgroundColor={colors.aurora[400]}
              isLoading={isSaveLoading}
              onClick={async () => {
                try {
                  const updatedQuery = _.cloneDeep(orgProvidedInputQuery);
                  updatedQuery.parameterConfig.reportingPeriod.reportingPeriod = reportingPeriod;

                  await onSave(updatedQuery);
                } catch (err) {
                  toast({
                    ...errorToastConfig,
                    id: 'saveError',
                    title: 'Error saving formula. Please try again.',
                  });
                } finally {
                  onEditClose();
                }
              }}
            >
              Save
            </Button>
          </Flex>
        </>
      ) : (
        <>
          <Flex
            width="100%"
            justifyContent="space-between"
            alignItems={
              initialReportingPeriod.period === 'calendarWeek'
                ? undefined
                : 'center'
            }
          >
            <Box>
              {initialReportingPeriod.period === 'calendarWeek' && (
                <Text fontWeight={500} mb="8px">
                  Period
                </Text>
              )}
              <Text>
                {commonUtils.toTitleCase(initialReportingPeriod.period)}
              </Text>
            </Box>
            <EditIconButton
              label="Edit"
              ariaLabel="edit"
              onClick={onEditOpen}
            />
          </Flex>
          {initialReportingPeriod.period === 'calendarWeek' && (
            <Box mt="24px">
              <Text fontWeight={500} mb="8px">
                Start Day Of Week
              </Text>
              <Text>{initialReportingPeriod.weekStartDay ?? '-'}</Text>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

type LenderOrgProvidedInputViewProps = {
  postgresQuery: CreditAgreement.Config.CommonPostgresQuery.Query.OrgProvidedInputQuery;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  duplicateParams: {
    isDuplicateQueryOpen: boolean;
    onDuplicateQueryOpen: () => void;
    onDuplicateQueryClose: () => void;
    duplicateQuery: (params: { queryKeyInput: string }) => Promise<void>;
    isDuplicateLoading: boolean;
  };
  deleteParams: {
    isDeleteConfirmationModalOpen: boolean;
    onDeleteConfirmationModalOpen: () => void;
    onDeleteConfirmationModalClose: () => void;
    deleteQuery: () => Promise<void>;
    isDeleteLoading: boolean;
  };
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  triggerDate?: Util.FullDateStr;
};
export const LenderOrgProvidedInputView = ({
  postgresQuery,
  wipConfig,
  fullPropertyKey,
  duplicateParams,
  deleteParams,
  onSave,
  isSaveLoading,
  presetVariables,
  inputDateType,
  triggerDate,
}: LenderOrgProvidedInputViewProps) => {
  const {
    isDuplicateQueryOpen,
    onDuplicateQueryOpen,
    onDuplicateQueryClose,
    duplicateQuery,
    isDuplicateLoading,
  } = duplicateParams;
  const {
    isDeleteConfirmationModalOpen,
    onDeleteConfirmationModalOpen,
    onDeleteConfirmationModalClose,
    deleteQuery,
    isDeleteLoading,
  } = deleteParams;

  const evaluatedDate = useMemo(() => {
    if (!triggerDate) {
      return undefined;
    }
    return commonFormulas.evaluateDateFormula({
      dateFormula: postgresQuery.parameterConfig.reportingDateFormula.formula,
      variables: [
        { name: 'triggerDate', value: triggerDate, parseNumber: true },
        ...postgresQuery.variables,
      ],
    });
  }, [postgresQuery, triggerDate]);

  return (
    <Box>
      <Flex alignItems="center" mb="24px" justifyContent="right">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="orgProvidedInput"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>
      <Divider />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="64px"
        mb="24px"
      >
        <Text fontWeight={600} fontSize="20px">
          Key
        </Text>
        <QueryInputTypeBadge type="orgProvidedInput" />
      </Flex>
      <Text
        color={colors.black[70]}
        p="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {postgresQuery.name}
      </Text>

      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Input
      </Text>
      <OrgProvidedInputKeysSelectField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        orgProvidedInputQuery={postgresQuery}
      />

      <Text fontWeight={600} fontSize="20px" mt="64px" mb="24px">
        Reporting Date
      </Text>
      <OrgProvidedInputReportingDateFormulaField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        orgProvidedInputQuery={postgresQuery}
        evaluatedDate={evaluatedDate}
        triggerDate={triggerDate}
      />

      <Flex mt="64px" mb="24px" alignItems="center">
        <Text fontWeight={600} fontSize="20px" mr="8px">
          Reporting Period
        </Text>
        <CalculationTerminologiesPopover termKeyLabel="reportingPeriod" />
      </Flex>
      <OrgProvidedInputReportingPeriodField
        wipConfig={wipConfig}
        fullPropertyKey={fullPropertyKey}
        onSave={onSave}
        isSaveLoading={isSaveLoading}
        orgProvidedInputQuery={postgresQuery}
      />

      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <PreviewPostgresQueryButton
          wipConfig={wipConfig}
          postgresQuery={postgresQuery}
          inputDateType={inputDateType}
          presetVariables={presetVariables}
          queryType={postgresQuery.type}
          reportingDate={evaluatedDate}
          reportingPeriod={
            postgresQuery.parameterConfig.reportingPeriod.reportingPeriod
          }
          orgProvidedInputKey={postgresQuery.parameterConfig.key.key}
          selectedInputDate={triggerDate}
        />
      )}
    </Box>
  );
};
